const REALTIME_TOKEN_ROUTE = '/api/transcribe-live/elevenlabs';

// ElevenLabs realtime tokens are single-use and typically valid for ~1 hour.
const TOKEN_MAX_AGE_MS = 45 * 60 * 1000;

class ElevenLabsRealtimeTokenService {
  private cachedToken: string | null = null;
  private cachedTokenAt = 0;
  private pendingFetch: Promise<string> | null = null;

  prefetch(): Promise<string | null> {
    this.kickoffPrefetch();

    if (this.pendingFetch) {
      return this.pendingFetch.then(token => token).catch(() => null);
    }

    return Promise.resolve(this.cachedToken);
  }

  async getToken(): Promise<string> {
    const now = Date.now();

    if (this.cachedToken && now - this.cachedTokenAt < TOKEN_MAX_AGE_MS) {
      const token = this.cachedToken;
      this.cachedToken = null;
      this.cachedTokenAt = 0;
      this.kickoffPrefetch();
      return token;
    }

    this.cachedToken = null;
    this.cachedTokenAt = 0;

    if (!this.pendingFetch) {
      this.kickoffPrefetch();
    }

    if (!this.pendingFetch) {
      throw new Error('Failed to initialize ElevenLabs realtime token prefetch.');
    }

    const token = await this.pendingFetch;
    this.cachedToken = null;
    this.cachedTokenAt = 0;
    this.kickoffPrefetch();
    return token;
  }

  private kickoffPrefetch() {
    if (this.pendingFetch || this.cachedToken) {
      return;
    }

    this.pendingFetch = this.fetchToken()
      .then(token => {
        this.cachedToken = token;
        this.cachedTokenAt = Date.now();
        this.pendingFetch = null;
        return token;
      })
      .catch(error => {
        this.pendingFetch = null;
        console.warn('[elevenlabs-token-service] Prefetch failed:', error);
        throw error;
      });
  }

  private async fetchToken(): Promise<string> {
    const response = await fetch(REALTIME_TOKEN_ROUTE, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
    });

    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(
        body.trim() || `Failed to fetch ElevenLabs realtime token (${response.status}).`
      );
    }

    const data = (await response.json().catch(() => ({}))) as { token?: unknown };
    const token = typeof data.token === 'string' ? data.token.trim() : '';

    if (!token) {
      throw new Error('Invalid ElevenLabs realtime token response.');
    }

    return token;
  }
}

export const elevenLabsRealtimeTokenService = new ElevenLabsRealtimeTokenService();
